import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectDescription, postProjectDescription } from '../../../../../actions/tigercat-burns/tigercat-base-actions';

const MonitoringAndReporting = ({ projectId }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({
        instrumentation: '',
        chain_of_custody: '',
        parameters_measured: '',
        collected_data: '',
        operational_data: '',
        data_frequency: '',
        data_archiving: '',
        data_qaqc: '',
    });

    const monitoringData = useSelector(state => state.tigercatBurnsBaseReducer.projectDescription.data);

    const handleSave = () => {
        dispatch(postProjectDescription(projectId, 'monitoring-and-reporting', form));
    };

    useEffect(() => {
        dispatch(getProjectDescription(projectId, 'monitoring-and-reporting'));
    }, [projectId, dispatch]);

    useEffect(() => {
        if (monitoringData) {
            setForm({
                instrumentation: monitoringData.instrumentation ?? '',
                chain_of_custody: monitoringData.chain_of_custody ?? '',
                parameters_measured: monitoringData.parameters_measured ?? '',
                collected_data: monitoringData.collected_data ?? '',
                operational_data: monitoringData.operational_data ?? '',
                data_frequency: monitoringData.data_frequency ?? '',
                data_archiving: monitoringData.data_archiving ?? '',
                data_qaqc: monitoringData.data_qaqc ?? '',
            });
        }
    }, [monitoringData]);

    const handleChange = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
    };

    return (
        <Card sx={{ maxWidth: '900px', mx: 'auto', mt: 4 }}>
            <Box sx={{ ml: 2 }}>
                <h2>Monitoring and Reporting</h2>
            </Box>
            <CardContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Describe the instrumentation used for monitoring and reporting.
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.instrumentation} onChange={handleChange('instrumentation')} sx={{ mb: 3 }}
                />

                <Typography variant="body2" sx={{ ml: 1, mb: 1 }}>
                    a. Data to be collected per chain of custody tracking requirements, including location data, and process for collecting and compiling such data.
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.chain_of_custody} onChange={handleChange('chain_of_custody')} sx={{ mb: 2 }}
                />

                <Typography variant="body2" sx={{ ml: 1, mb: 1 }}>
                    b. Describe the parameters to be measured, including any parameters additional to those specified in Table 6.6 of the Protocol.
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.parameters_measured} onChange={handleChange('parameters_measured')} sx={{ mb: 2 }}
                />

                <Typography variant="body2" sx={{ ml: 1, mb: 1 }}>
                    c. Describe the data to be collected and data collection techniques and sample designs for directly sampled parameters, per requirements outlined in Section 6.3 of the Protocol.
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.collected_data} onChange={handleChange('collected_data')} sx={{ mb: 3 }}
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                    Describe the operational data to be documented, including biochar production conditions.
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.operational_data} onChange={handleChange('operational_data')} sx={{ mb: 3 }}
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                    Describe the frequency of data acquisition. At a minimum, the data required for quantification of biochar projects shall be monitored and recorded (or documented, as appropriate) for each reporting period.
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.data_frequency} onChange={handleChange('data_frequency')} sx={{ mb: 3 }}
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                    Describe the data archiving procedures (see Section 7.3 of the Protocol for minimum record keeping requirements)
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.data_archiving} onChange={handleChange('data_archiving')} sx={{ mb: 3 }}
                />

                <Typography variant="body1" sx={{ mb: 1 }}>
                    Describe the QA/QC provisions in place to ensure that data acquisition, compilation, and reporting are carried out consistently and with precision (where relevant).
                </Typography>
                <TextField
                    fullWidth multiline minRows={3} placeholder="Add text"
                    value={form.data_qaqc} onChange={handleChange('data_qaqc')} sx={{ mb: 3 }}
                />

                <Button variant="contained" onClick={handleSave}>
                    Save
                </Button>
            </CardContent>
        </Card>
    );
};

export default MonitoringAndReporting;